/**
 * A case's flags on a row, with the one a person decides toggleable in place
 * (D-0066): clicking the flags opens a menu that lists every flag the case
 * carries, Urgent as a checkbox that sets or clears it through the hub's
 * PUT, the engine's flags (Repeat DNA, Late cancellation, No appointment,
 * DNA fee suggested) and the clinic's Prior DNA as read-only lines with
 * their meaning. A row with no flag shows a quiet "Flags" affordance so
 * Urgent can be set without opening the case. The trigger swallows the
 * click so the row's link does not fire. Mounted on the pipeline, the QA
 * and doctor homes and the clinic (list rows, week cards, phone cards).
 */

import { cn } from 'cn';
import { useOnline } from '@/app/online';
import type { MatterFlags } from '@/app/types';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useSetUrgent } from '@/features/matter/useMatter';
import { FlagText, activeFlags, flagLabel, type FlagKind } from './FlagText';

const MEANING: Record<Exclude<FlagKind, 'urgent'>, string> = {
  repeatDna: 'The engine raised it: a second DNA on this case.',
  lateCancellation: 'The engine raised it: cancelled inside the late window.',
  dnaFeeSuggested: 'The engine suggests a DNA fee; the invoice is the practice\'s.',
  skippedAppointment: 'No appointment: a file review, or the referrer asked for none.',
  priorDna: 'A DNA on an earlier cycle of this case.',
};

export type FlagMenuProps = {
  matterRef: string;
  flags: Partial<MatterFlags> & { priorDna?: boolean };
  /** How many flag words to show on the row before the rest fold into the menu; the phone shows all. */
  max?: number;
  className?: string;
  /** Stack the words (a 46 px row keeps two lines) or run them inline (a card). */
  layout?: 'stack' | 'inline';
};

export function FlagMenu({ matterRef, flags, max = 2, className, layout = 'stack' }: FlagMenuProps) {
  const online = useOnline();
  const setUrgent = useSetUrgent();
  const kinds = activeFlags(flags);
  const shown = kinds.slice(0, max);
  const more = kinds.length - shown.length;
  const busy = setUrgent.isPending && setUrgent.variables?.ref === matterRef;
  const label = kinds.length > 0 ? `Flags for ${matterRef}: ${kinds.map(flagLabel).join(', ')}` : `Flags for ${matterRef}: none`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-slot="flag-menu"
          data-urgent={flags.urgent ? 'true' : undefined}
          aria-label={label}
          aria-busy={busy || undefined}
          className={cn('flex min-h-7 items-center rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-beam-signature', layout === 'stack' ? 'flex-col items-start gap-0.5' : 'flex-wrap gap-2', className)}
          onClick={(e) => e.stopPropagation()}
        >
          {kinds.length === 0 ? <span className="text-xs text-beam-text-faint">Flags</span> : shown.map((k) => <FlagText key={k} kind={k} />)}
          {more > 0 ? <span className="text-xs text-beam-text-muted">+{more}</span> : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel className="tabular">{matterRef}</DropdownMenuLabel>
        <DropdownMenuCheckboxItem checked={Boolean(flags.urgent)} disabled={!online || setUrgent.isPending} onCheckedChange={(checked) => setUrgent.mutate({ ref: matterRef, urgent: checked === true })} onSelect={(e) => e.preventDefault()}>
          Urgent
        </DropdownMenuCheckboxItem>
        {kinds.filter((k) => k !== 'urgent').length > 0 ? <DropdownMenuSeparator /> : null}
        {kinds.filter((k) => k !== 'urgent').map((k) => (
          // Read only, not dimmed: the item's own data-disabled:opacity-50 would fade the meaning to half contrast.
          <DropdownMenuItem key={k} disabled className="flex flex-col items-start gap-0.5 data-disabled:opacity-100">
            <FlagText kind={k} />
            <span className="text-xs text-beam-text-muted">{MEANING[k as Exclude<FlagKind, 'urgent'>]}</span>
          </DropdownMenuItem>
        ))}
        {!online ? <><DropdownMenuSeparator /><DropdownMenuItem disabled className="text-xs data-disabled:opacity-100">Paused while offline</DropdownMenuItem></> : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
